import React, { useRef, useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { usePageEditMode } from '@/context/PageEditModeContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authHeaders = () => {
  const token = localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const CREATE_FIELDS = [
  'name', 'description', 'category', 'base_price', 'images', 'thumbnail',
  'variants', 'sizes_available', 'colors_available', 'is_custom_order',
  'estimated_delivery_days', 'low_stock_threshold', 'featured', 'tags',
];

const pickPayload = (costume, overrides = {}) => {
  const out = {};
  for (const k of CREATE_FIELDS) {
    out[k] = overrides[k] !== undefined ? overrides[k] : costume[k];
  }
  if (out.base_price !== undefined && out.base_price !== null) {
    out.base_price = Number(out.base_price) || 0;
  }
  if (out.estimated_delivery_days !== undefined) {
    out.estimated_delivery_days = Number(out.estimated_delivery_days) || 14;
  }
  if (out.low_stock_threshold !== undefined) {
    out.low_stock_threshold = Number(out.low_stock_threshold) || 0;
  }
  // Fill arrays if missing
  for (const arrK of ['images', 'variants', 'sizes_available', 'colors_available', 'tags']) {
    if (out[arrK] == null) out[arrK] = [];
  }
  return out;
};

/**
 * Inline-editable costume product field.
 * When the page is in edit mode, admins can click the field, edit text,
 * and the change is PUT to /api/costumes/admin/{id} on blur.
 *
 * Props:
 *   costume       the costume object (must include id and all fields)
 *   field         field name on the costume (e.g. "name", "base_price", "category", "description")
 *   as            HTML tag
 *   className     passed through
 *   formatter     (value) => string, used only when NOT editing (e.g. price → "$150.00")
 *   parser        (text) => value, inverse of formatter for saving (defaults to string)
 *   onSaved       (updatedCostume) => void — called after a successful save
 */
export const InlineCostumeField = ({
  costume,
  field,
  as = 'span',
  className = '',
  formatter,
  parser,
  onSaved,
  children,
  ...rest
}) => {
  const { enabled } = usePageEditMode();
  const Tag = as;
  const ref = useRef(null);
  const [saving, setSaving] = useState(false);
  const [localValue, setLocalValue] = useState(costume?.[field]);

  useEffect(() => {
    setLocalValue(costume?.[field]);
  }, [costume, field]);

  const displayValue = formatter
    ? formatter(localValue, costume)
    : (localValue == null ? '' : String(localValue));

  // Keep the DOM text in sync in edit mode so we don't blow away the user's caret.
  useEffect(() => {
    if (enabled && ref.current && ref.current.textContent !== displayValue) {
      ref.current.textContent = displayValue;
    }
  }, [enabled, displayValue]);

  if (!enabled) {
    return <Tag className={className} {...rest}>{displayValue}</Tag>;
  }

  const stop = (e) => e.stopPropagation();

  const commit = async (e) => {
    const text = (e.currentTarget.textContent || '').trim();
    const parsed = parser ? parser(text) : text;
    if (parsed === localValue) return;

    const prev = localValue;
    setLocalValue(parsed);
    setSaving(true);
    try {
      const payload = pickPayload(costume, { [field]: parsed });
      const res = await axios.put(
        `${API}/costumes/admin/${costume.id}`,
        payload,
        { headers: authHeaders() }
      );
      if (res.data?.success === false) throw new Error('Save failed');
      toast.success('Saved');
      if (onSaved) onSaved({ ...costume, [field]: parsed });
    } catch (err) {
      setLocalValue(prev);
      if (ref.current) ref.current.textContent = formatter ? formatter(prev, costume) : String(prev ?? '');
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Tag
      ref={ref}
      className={`${className} outline-dashed outline-2 outline-yellow-400/70 outline-offset-2 rounded-sm cursor-text focus:outline-primary focus:outline-solid ${saving ? 'opacity-60' : ''}`}
      contentEditable
      suppressContentEditableWarning
      spellCheck
      onMouseDown={stop}
      onClick={stop}
      onPointerDown={stop}
      onBlur={commit}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === 'Enter') {
          e.preventDefault();
          e.currentTarget.blur();
        }
      }}
      data-edit-path={`costume.${costume?.id}.${field}`}
      data-edit-costume-id={costume?.id}
      data-edit-field={field}
      {...rest}
    >
      {displayValue}
    </Tag>
  );
};

export default InlineCostumeField;
